<script setup lang="ts">
import { computed, useId } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { provideFormFieldContext } from '@uiKit/composables/useFormFieldContext'

interface Props {
  label?: string
  required?: boolean
  optional?: boolean
  error?: string
  helperText?: string
}

const props = withDefaults(defineProps<Props>(), {
  required: false,
  optional: false,
})

// Ties the label and message to the slotted control (see useFormFieldContext).
const baseId = useId()
const controlId = `${baseId}-control`
const labelId = `${baseId}-label`
const messageId = `${baseId}-message`
provideFormFieldContext({
  controlId,
  labelId,
  describedBy: computed(() => (props.error || props.helperText ? messageId : undefined)),
  invalid: computed(() => Boolean(props.error)),
})
</script>

<template>
  <Box direction="col" gap="1" class="form-field">
    <Text v-if="label" :id="labelId" is="label" :for="controlId" type="body" color="muted">
      {{ label }}
      <Text v-if="required" is="span" type="body" color="danger" aria-hidden="true">*</Text>
      <Text v-else-if="optional" is="span" type="body" color="muted">(optional)</Text>
    </Text>

    <slot />

    <Box
      v-if="error"
      :id="messageId"
      align="center"
      gap="1"
      class="form-field__error"
      role="alert"
    >
      <PiIcon name="exclamation-circle" class="form-field__error-icon" />
      <Text type="caption" color="danger">{{ error }}</Text>
    </Box>
    <Text v-else-if="helperText" :id="messageId" type="caption" color="muted">
      {{ helperText }}
    </Text>
  </Box>
</template>

<style scoped>
.form-field {
  width: 100%;
}

.form-field__error {
  margin-top: 0.125rem;
}

.form-field__error-icon {
  color: var(--p-danger-600);
  font-size: 12px;
}
</style>
